/**
 * UserLandingPage Component (Staff Portal)
 * 
 * Main dashboard for staff users showing quotes/orders with search functionality.
 * Allows staff to search, view, edit, and copy orders.
 * NOTE: Behavior copied from legacy App.js, do not change without regression testing.
 * 
 * @component
 * @param {Object} props
 * @param {Function} props.onStartNew - Callback to create new order
 * @param {Function} props.onEditOrder - Callback to edit order
 * @param {Function} props.onViewOrder - Callback to view order details
 * @param {Function} props.onCopyOrder - Callback to copy order
 * @param {Function} props.onLogout - Callback to logout
 * @param {Object} props.currentStore - Current store data
 * @param {Object} props.currentUser - Current logged-in user
 * @param {Object} props.subscriptionStatus - Subscription status
 * @param {Function} props.onUpgrade - Callback to upgrade subscription
 * @param {boolean} props.canEditConfirmedOrders - Whether user can edit confirmed orders (store_admin)
 */

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Plus, Search, Home, Eye, Edit, FileText, MoreVertical, Trash2, Copy } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../utils/constants';
import { formatMonDD, getImageUrl } from '../../utils/formatters';
import FloatingContactButton from '../shared/FloatingContactButton';

// Import subscription components
import TrialBanner from '../subscription/TrialBanner';

function UserLandingPage({ onStartNew, onEditOrder, onViewOrder, onCopyOrder, onLogout, currentStore, currentUser, subscriptionStatus, onUpgrade, canEditConfirmedOrders = false }) {
  // Helper function to get store ID
  const getStoreId = () => {
    return currentUser?.storeId || currentStore?.storeId || currentStore?.id;
  };
  
  // Check if user is store_admin (manager) - can edit confirmed orders
  const isManager = canEditConfirmedOrders || currentUser?.role === 'store_admin';

  // Preserve state in sessionStorage for navigation back
  const [searchQuery, setSearchQuery] = useState(() => sessionStorage.getItem('staff_searchQuery') || '');
  const [searchResults, setSearchResults] = useState(() => {
    const saved = sessionStorage.getItem('staff_searchResults');
    return saved ? JSON.parse(saved) : [];
  });
  const [searching, setSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(() => sessionStorage.getItem('staff_hasSearched') === 'true');
  const [quoteOrders, setQuoteOrders] = useState(() => {
    const saved = sessionStorage.getItem('staff_quoteOrders');
    return saved ? JSON.parse(saved) : [];
  });
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [viewMode, setViewMode] = useState(() => sessionStorage.getItem('staff_viewMode') || 'quotes');
  
  const [sortBy, setSortBy] = useState('event_date');
  const [sortOrder, setSortOrder] = useState('asc');
  const [discountDisplayMode, setDiscountDisplayMode] = useState('amount'); // 'amount' or 'percentage'
  const [bulkEditMode, setBulkEditMode] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  

  // Check if user is staff OR manager (both use the Staff/Manager Portal)
  const isStaffOrManager = currentUser?.role === 'store_staff' || currentUser?.role === 'store_admin';
  
  // Save state to sessionStorage whenever it changes
  useEffect(() => {
    sessionStorage.setItem('staff_searchQuery', searchQuery);
  }, [searchQuery]);
  
  useEffect(() => {
    sessionStorage.setItem('staff_searchResults', JSON.stringify(searchResults));
  }, [searchResults]);
  
  useEffect(() => {
    sessionStorage.setItem('staff_hasSearched', hasSearched);
  }, [hasSearched]);
  
  useEffect(() => {
    sessionStorage.setItem('staff_quoteOrders', JSON.stringify(quoteOrders));
  }, [quoteOrders]);
  
  useEffect(() => {
    sessionStorage.setItem('staff_viewMode', viewMode);
  }, [viewMode]);

  // Fetch relevant orders for Staff/Manager - Quotes + Confirmed orders for today/next 2 days
  const fetchQuoteOrders = async () => {
    if (!isStaffOrManager) return;
    
    try {
      setLoadingQuotes(true);
      const storeId = getStoreId();
      console.log('=== FETCHING STAFF/MANAGER RELEVANT ORDERS ===');
      console.log('Current Store:', currentStore);
      console.log('Store ID for query:', storeId);
      const apiUrl = `${API}/orders${storeId ? `?store_id=${storeId}` : ''}`;
      console.log('API URL:', apiUrl);
      const response = await axios.get(apiUrl);
      console.log('Orders response:', response.data);
      console.log('Number of orders returned:', response.data.length);
      
      // Get today and next 2 days (total 3 days window)
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const dayAfterTomorrow = new Date(today);
      dayAfterTomorrow.setDate(today.getDate() + 2);
      dayAfterTomorrow.setHours(23, 59, 59, 999);
      
      // Filter for:
      // 1. All Quote status orders
      // 2. Confirmed orders with event date today or in next 2 days
      const filtered = response.data.filter(order => {
        const isQuote = order.status?.toLowerCase() === 'quote';
        const isConfirmed = order.status?.toLowerCase() === 'confirmed';
        
        if (isQuote) {
          // Include all quote orders
          return true;
        }
        
        if (isConfirmed) {
          // For confirmed orders, check if event date is within next 2 days
          const eventDateStr = order.customer_info?.start_date;
          if (!eventDateStr) return false;
          
          // Parse date without timezone issues
          const [year, month, day] = eventDateStr.split('T')[0].split('-').map(Number);
          const eventDate = new Date(year, month - 1, day);
          eventDate.setHours(12, 0, 0, 0); // Set to noon to avoid timezone edge cases
          
          // Check if event date is between today and day after tomorrow
          return eventDate >= today && eventDate <= dayAfterTomorrow;
        }
        
        return false;
      });
      
      console.log('Filtered orders (quotes + upcoming confirmed):', filtered.length);
      
      // Sort by event date ascending (soonest first)
      filtered.sort((a, b) => {
        const dateA = new Date(a.customer_info?.start_date || a.created_at);
        const dateB = new Date(b.customer_info?.start_date || b.created_at);
        return dateA - dateB;
      });
      
      setQuoteOrders(filtered);
    } catch (error) {
      console.error('Failed to fetch relevant orders:', error);
    } finally {
      setLoadingQuotes(false);
    }
  };
  
  // Load relevant orders on mount for staff/manager - WAIT for currentStore to be set
  useEffect(() => {
    if (isStaffOrManager && currentStore) {
      console.log('Triggering fetchQuoteOrders with store:', currentStore?.name);
      fetchQuoteOrders();
    } else if (isStaffOrManager && !currentStore) {
      console.log('Staff/Manager detected but currentStore is null - waiting...');
    }
  }, [isStaffOrManager, currentStore]);


  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  // Helper function to get full datetime for sorting (combines date + time)
  const getEventDateTime = (order) => {
    const dateStr = order.customer_info?.start_date || '9999-12-31';
    const timeStr = getDeliveryTimeRaw(order); // Get time in 24h format for sorting
    
    if (timeStr) {
      // Combine date and time for proper chronological sorting
      return new Date(`${dateStr.split('T')[0]}T${timeStr}:00`);
    }
    return new Date(dateStr);
  };

  // Helper to get raw time in 24h format for sorting
  const getDeliveryTimeRaw = (order) => {
    // First try customer_info.event_time
    if (order.customer_info?.event_time) {
      const time = order.customer_info.event_time;
      // If already in 24h format, return as-is
      if (!time.includes('AM') && !time.includes('PM')) {
        return time;
      }
      // Convert 12h to 24h for sorting
      const [timePart, period] = time.split(' ');
      let [hours, minutes] = timePart.split(':');
      hours = parseInt(hours, 10);
      if (period === 'PM' && hours !== 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      return `${hours.toString().padStart(2, '0')}:${minutes}`;
    }
    
    // Try day_meal_selections
    const daySelections = order.customer_info?.day_meal_selections || order.day_meal_selections || order.event_days;
    if (daySelections && daySelections.length > 0) {
      const firstDay = daySelections[0];
      if (firstDay.meal_times && firstDay.meal_times.length > 0) {
        return firstDay.meal_times[0].delivery_time || null;
      }
    }
    return null;
  };
  
  const getSortedOrders = (orders) => {
    return [...orders].sort((a, b) => {
      let aVal, bVal;
      
      switch(sortBy) {
        case 'created_at':
          aVal = new Date(a.created_at);
          bVal = new Date(b.created_at);
          break;
        case 'event_date':
          // Use combined date + time for chronological sorting
          aVal = getEventDateTime(a);
          bVal = getEventDateTime(b);
          break;
        case 'customer_name':
          aVal = (a.customer_info?.name || '').toLowerCase();
          bVal = (b.customer_info?.name || '').toLowerCase();
          break;
        case 'phone':
          aVal = a.customer_info?.phone || '';
          bVal = b.customer_info?.phone || '';
          break;
        case 'status':
          aVal = a.status || '';
          bVal = b.status || '';
          break;
        case 'amount':
          aVal = a.total_amount || 0;
          bVal = b.total_amount || 0;
          break;
        case 'discount':
          aVal = a.discount_amount || 0;
          bVal = b.discount_amount || 0;
          break;
        case 'balance':
          aVal = a.balance_amount !== undefined ? a.balance_amount : (a.total_amount - (a.advance_payment || 0) - (a.discount_amount || 0));
          bVal = b.balance_amount !== undefined ? b.balance_amount : (b.total_amount - (b.advance_payment || 0) - (b.discount_amount || 0));
          break;
        case 'payment':
          aVal = (a.balance_amount !== undefined ? a.balance_amount : (a.total_amount - (a.advance_payment || 0) - (a.discount_amount || 0))) === 0 ? 0 : 1;
          bVal = (b.balance_amount !== undefined ? b.balance_amount : (b.total_amount - (b.advance_payment || 0) - (b.discount_amount || 0))) === 0 ? 0 : 1;
          break;
        default:
          return 0;
      }
      
      if (aVal < bVal) return sortOrder === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });
  };

  // Helper function to get delivery/pickup time from order
  const getDeliveryTime = (order) => {
    // First try to get time from customer_info.event_time (newer format)
    if (order.customer_info?.event_time) {
      const deliveryTime = order.customer_info.event_time;
      // Check if already in 12h format
      if (deliveryTime.includes('AM') || deliveryTime.includes('PM')) {
        return deliveryTime;
      }
      // Convert 24h format to 12h format (e.g., "18:00" -> "6:00 PM")
      const [hours, minutes] = deliveryTime.split(':');
      const hour = parseInt(hours, 10);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const hour12 = hour % 12 || 12;
      return `${hour12}:${minutes} ${ampm}`;
    }
    
    // Try day_meal_selections (current format used in order creation)
    const daySelections = order.customer_info?.day_meal_selections || order.day_meal_selections || order.event_days;
    if (daySelections && daySelections.length > 0) {
      const firstDay = daySelections[0];
      if (firstDay.meal_times && firstDay.meal_times.length > 0) {
        const deliveryTime = firstDay.meal_times[0].delivery_time;
        if (deliveryTime) {
          const [hours, minutes] = deliveryTime.split(':');
          const hour = parseInt(hours, 10);
          const ampm = hour >= 12 ? 'PM' : 'AM';
          const hour12 = hour % 12 || 12;
          return `${hour12}:${minutes} ${ampm}`;
        }
      }
    }
    return null;
  };


  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error('Please enter search criteria');
      return;
    }

    try {
      setSearching(true);
      setHasSearched(true);
      setSearchResults([]); // Clear previous results FIRST
      
      // Fetch all orders using correct API constant
      const storeId = getStoreId();
      const response = await axios.get(`${API}/orders${storeId ? `?store_id=${storeId}` : ''}`);
      const query = searchQuery.toLowerCase().trim();
      
      console.log('Total orders fetched:', response.data.length);
      
      // Filter results by phone, name, or order ID
      const filtered = response.data.filter(order => {
        const orderPhone = String(order.customer_info.phone || '').trim().toLowerCase();
        const orderName = String(order.customer_info.name || '').trim().toLowerCase();
        const orderId = String(order.id || '').trim().toLowerCase();
        
        console.log(`Comparing query "${query}" with phone "${orderPhone}"`);
        
        const matchesPhone = orderPhone.includes(query);
        const matchesName = orderName.includes(query);
        const matchesOrderId = orderId.includes(query);
        
        return matchesPhone || matchesName || matchesOrderId;
      });
      
      // Sort by event date (most recent first)
      filtered.sort((a, b) => {
        const dateA = a.customer_info.start_date || a.customer_info.event_date || '';
        const dateB = b.customer_info.start_date || b.customer_info.event_date || '';
        return dateB.localeCompare(dateA); // Descending order
      });
      
      console.log('Filtered results:', filtered.length);
      setSearchResults(filtered);
      setViewMode('search'); // Switch to search view
      
      if (filtered.length === 0) {
        toast.info('No orders found matching your search');
      } else {
        toast.success(`Found ${filtered.length} order(s)`);
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Search failed. Please try again.');
    } finally {
      setSearching(false);
    }
  };

  const canEdit = (order) => {
    // Staff can only edit quote orders
    // Manager (store_admin) can edit both quote AND confirmed orders
    if (order.status === 'quote') {
      return true;
    }
    if (order.status === 'confirmed' && isManager) {
      return true;
    }
    return false;
  };

  const handleDelete = async (orderId, orderName) => {
    if (!window.confirm(`Are you sure you want to delete order for ${orderName}? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/orders/${orderId}`);
      toast.success('Order deleted successfully');
      
      // Refresh the appropriate view based on current state
      if (viewMode === 'search' && searchQuery) {
        // Re-run search to refresh search results
        handleSearch();
      } else if (viewMode === 'quotes') {
        // Refresh quote orders list
        fetchQuoteOrders();
      }
    } catch (error) {
      console.error('Failed to delete order:', error);
      toast.error('Failed to delete order');
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bawarchi-background-muted)' }}>
      {/* Trial Banner */}
      <TrialBanner subscriptionStatus={subscriptionStatus} onUpgrade={onUpgrade} />
      
      {/* Sticky Navigation */}
      <div className="nav-bawarchi">
        <div className="nav-brand flex items-center gap-2">
          {currentStore?.logoUrl && (
            <img src={getImageUrl(currentStore.logoUrl)} alt="Store Logo" className="h-16 w-auto" />
          )}
          <div className="flex flex-col">
            <span className="font-semibold">{currentStore?.name || 'CaterLog'}</span>
            {currentStore?.tagline && (
              <span className="text-sm text-gray-600">{currentStore.tagline}</span>
            )}
          </div>
        </div>
        <button onClick={onLogout} className="btn-bawarchi-secondary" style={{ padding: '8px 20px', minHeight: '40px' }}>
          <Home className="w-4 h-4 mr-2 inline" />
          Logout
        </button>
      </div>

      <div className="page-container">
        <div className="page-header text-center">
          <h1 className="page-title">{isManager ? 'Manager Portal' : 'Staff Portal'}</h1>
          <p className="page-subtitle">{isManager ? 'Search, manage, and edit orders' : 'Search and manage your orders'}</p>
        </div>

        <div className="max-w-6xl mx-auto space-y-6">
          {/* Action Button - Centered and Sized Like Admin Portal */}
          <div className="flex justify-center mb-8">
            <button
              onClick={onStartNew}
              className="btn-bawarchi-primary flex items-center gap-2"
              style={{padding: '10px 20px', minHeight: '44px', fontSize: '15px'}}
            >
              <Plus className="w-4 h-4" />
              Create New Order
            </button>
          </div>

          {/* Search Section - At Top */}
          <Card>
            <CardHeader>
              <CardTitle>Search Orders</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-3">
                <input
                  type="text"
                  placeholder="Search by phone, customer name, or order ID..."
                  className="input-bawarchi flex-1"
                  value={searchQuery}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSearchQuery(value);
                    // Clear search results when search query is cleared
                    if (!value.trim()) {
                      setSearchResults([]);
                      setViewMode('quotes');
                      setHasSearched(false);
                    }
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  disabled={searching || !searchQuery.trim()}
                  className="btn-bawarchi-primary"
                  style={{minHeight: '48px', minWidth: '120px'}}
                >
                  {searching ? 'Searching...' : 'Search'}
                </button>
              </div>
            </CardContent>
          </Card>

          {/* Toggle View Buttons + Single Orders List */}
          {isStaffOrManager && (
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {viewMode === 'quotes' ? `Quotes & Upcoming Orders (${quoteOrders.length})` : `Search Results (${searchResults.length})`}
                  </CardTitle>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setViewMode('quotes');
                        fetchQuoteOrders();
                      }}
                      className={viewMode === 'quotes' ? 'btn-bawarchi-primary' : 'btn-bawarchi-secondary'}
                      style={{padding: '8px 16px', minHeight: '40px', fontSize: '14px'}}
                    >
                      Upcoming Orders
                    </button>
                    <button
                      onClick={() => setViewMode('search')}
                      className={viewMode === 'search' ? 'btn-bawarchi-primary' : 'btn-bawarchi-secondary'}
                      style={{padding: '8px 16px', minHeight: '40px', fontSize: '14px'}}
                      disabled={!hasSearched}
                    >
                      Search Results
                    </button>
                  </div>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Showing all quote orders + confirmed orders for today and next 2 days
                </p>
              </CardHeader>
              <CardContent>
                {(viewMode === 'quotes' && loadingQuotes) ? (
                  <div className="text-center py-8">
                    <p className="text-muted">Loading orders...</p>
                  </div>
                ) : (viewMode === 'quotes' && quoteOrders.length === 0) ? (
                  <div className="text-center py-8 bg-gray-50 rounded border border-gray-200">
                    <FileText className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No quotes or upcoming confirmed orders</p>
                  </div>
                ) : (viewMode === 'search' && searchResults.length === 0) ? (
                  <div className="text-center py-8 bg-gray-50 rounded border border-gray-200">
                    <Search className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">No results found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b border-orange-200">
                          <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('customer_name')}>
                            <span>Customer {sortBy === 'customer_name' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('phone')}>
                            <span>Phone {sortBy === 'phone' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('created_at')}>
                            <span>Created {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('event_date')}>
                            <span>Event {sortBy === 'event_date' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('status')}>
                            <span>Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-right p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('amount')}>
                            <span>Total {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-right p-3 text-orange-700 font-semibold whitespace-nowrap">
                            <div className="flex items-center justify-end gap-2">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDiscountDisplayMode(discountDisplayMode === 'amount' ? 'percentage' : 'amount');
                                }}
                                className="text-xs px-2 py-1 rounded border border-orange-300 bg-orange-50 hover:bg-orange-100 transition-colors"
                                title={`Switch to ${discountDisplayMode === 'amount' ? 'percentage' : 'amount'} view`}
                              >
                                {discountDisplayMode === 'amount' ? '$' : '%'}
                              </button>
                              <span className="cursor-pointer hover:text-orange-800" onClick={() => handleSort('discount')}>
                                Promo {sortBy === 'discount' && (sortOrder === 'asc' ? '↑' : '↓')}
                              </span>
                            </div>
                          </th>
                          <th className="text-right p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('advance')}>
                            <span>Advance {sortBy === 'advance' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-right p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('balance')}>
                            <span>Balance {sortBy === 'balance' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-center p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('payment')}>
                            <span>Paid? {sortBy === 'payment' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                          </th>
                          <th className="text-center p-3 text-orange-700 font-semibold whitespace-nowrap">⋮</th>
                        </tr>
                      </thead>
                      <tbody>
                        {getSortedOrders(viewMode === 'quotes' ? quoteOrders : searchResults).map((order) => {
                          const balance = order.balance_amount !== undefined ? order.balance_amount : (order.total_amount - (order.advance_payment || 0) - (order.discount_amount || 0));
                          const isPaid = balance <= 0.01; // Allow for floating point tolerance
                          const isQuote = order.status?.toLowerCase() === 'quote';
                          
                          return (
                          <tr key={order.id} className="border-b border-gray-200 hover:bg-orange-50">
                            <td className="p-3">{order.customer_info?.name || 'N/A'}</td>
                            <td className="p-3">{order.customer_info?.phone || 'N/A'}</td>
                            <td className="p-3">{formatMonDD(order.created_at)}</td>
                            <td className="p-3 whitespace-nowrap">
                              {formatMonDD(order.customer_info?.start_date)}
                              {getDeliveryTime(order) && (
                                <span className="text-orange-600 ml-1">{getDeliveryTime(order)}</span>
                              )}
                            </td>
                            <td className="p-3">
                              {isQuote ? (
                                <button
                                  onClick={async (e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    try {
                                      const response = await axios.put(`${API}/orders/${order.id}/confirm-quote`);
                                      // Refresh the view
                                      if (viewMode === 'quotes') {
                                        fetchQuoteOrders();
                                      } else {
                                        handleSearch();
                                      }
                                    } catch (error) {
                                      console.error('Failed to confirm order:', error);
                                    }
                                  }}
                                  className="px-2 py-1 rounded text-xs font-medium bg-yellow-100 text-yellow-700 hover:bg-yellow-200 cursor-pointer transition-colors"
                                  title="Click to confirm"
                                >
                                  Quote ↻
                                </button>
                              ) : (
                                <span className={`px-2 py-1 rounded text-xs font-medium whitespace-nowrap ${
                                  order.status?.toLowerCase() === 'confirmed' ? 'bg-green-100 text-green-700' :
                                  order.status?.toLowerCase() === 'completed' ? 'bg-gray-100 text-gray-700' :
                                  order.status?.toLowerCase() === 'cancelled' ? 'bg-red-100 text-red-700' :
                                  'bg-blue-100 text-blue-700'
                                }`}>
                                  {order.status || 'Pending'}{order.status?.toLowerCase() === 'confirmed' && ' ✓'}
                                </span>
                              )}
                            </td>
                            <td className="p-3 text-right font-semibold">
                              ${Math.floor((order.subtotal || 0) + (order.service_fee || 0))}
                            </td>
                            <td className="p-3 text-right text-orange-600">
                              {(() => {
                                if (discountDisplayMode === 'percentage') {
                                  // Calculate percentage: (discount_amount / subtotal) * 100
                                  // Subtotal = total_amount + discount_amount (grand total before discount)
                                  const subtotal = (order.total_amount || 0) + (order.discount_amount || 0);
                                  const percentage = subtotal > 0 ? ((order.discount_amount || 0) / subtotal) * 100 : 0;
                                  return percentage > 0 ? `-${percentage.toFixed(1)}%` : '0%';
                                } else {
                                  return order.discount_amount > 0 ? `-$${Math.floor(order.discount_amount)}` : '$0';
                                }
                              })()}
                            </td>
                            <td className="p-3 text-right text-blue-600 font-medium">
                              ${Math.floor(order.advance_payment || 0)}
                            </td>
                            <td className={`p-3 text-right font-bold ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                              ${Math.floor(balance)}
                            </td>
                            <td className="p-3 text-center">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  try {
                                    await axios.put(`${API}/orders/${order.id}/toggle-payment`);
                                    // Refresh the view
                                    if (viewMode === 'quotes') {
                                      fetchQuoteOrders();
                                    } else {
                                      handleSearch();
                                    }
                                  } catch (error) {
                                    console.error('Failed to toggle payment:', error);
                                    toast.error('Failed to toggle payment status');
                                  }
                                }}
                                className={`h-7 px-2 text-xs font-semibold ${
                                  isPaid 
                                    ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' 
                                    : 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200'
                                }`}
                              >
                                {isPaid ? '✓ PAID' : '⚠️ UNPAID'}
                              </Button>
                            </td>
                            <td className="p-3">
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    className="h-8 w-8 p-0"
                                  >
                                    <MoreVertical className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  <DropdownMenuItem onClick={() => {
                                    const currentList = viewMode === 'quotes' ? getSortedOrders(quoteOrders) : getSortedOrders(searchResults);
                                    const currentIndex = currentList.findIndex(o => o.id === order.id);
                                    onViewOrder(order.id, currentList, currentIndex);
                                  }}>
                                    <Eye className="mr-2 h-4 w-4" />
                                    View
                                  </DropdownMenuItem>
                                  {canEdit(order) && (
                                    <DropdownMenuItem onClick={() => onEditOrder(order.id)}>
                                      <Edit className="mr-2 h-4 w-4" />
                                      Edit
                                    </DropdownMenuItem>
                                  )}
                                  <DropdownMenuItem onClick={() => onCopyOrder(order.id)}>
                                    <Copy className="mr-2 h-4 w-4" />
                                    Copy
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={async () => {
                                      try {
                                        if (window.confirm(`Are you sure you want to delete order for ${order.customer_info?.name}?`)) {
                                          await axios.delete(`${API}/orders/${order.id}`);
                                          toast.success('Order deleted successfully');
                                          if (viewMode === 'quotes') {
                                            fetchQuoteOrders();
                                          } else {
                                            handleSearch();
                                          }
                                        }
                                      } catch (error) {
                                        console.error('Failed to delete order:', error);
                                        toast.error('Failed to delete order');
                                      }
                                    }}
                                    className="text-red-600"
                                  >
                                    <Trash2 className="mr-2 h-4 w-4" />
                                    Delete
                                  </DropdownMenuItem>
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Search Results for Non-Staff/Manager (old behavior - kept for backward compatibility) */}
          {!isStaffOrManager && hasSearched && (
            <div className="card-bawarchi">
              <h2 className="card-bawarchi-title">Search Results</h2>
              {searchResults.length > 0 ? (
                <div className="overflow-x-auto mt-4">
                  <div className="mb-2 text-sm text-green-700 bg-green-50 p-2 rounded">
                    Found {searchResults.length} order(s)
                  </div>
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-orange-200">
                        <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('customer_name')}>
                          <span>Customer {sortBy === 'customer_name' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('phone')}>
                          <span>Phone {sortBy === 'phone' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('created_at')}>
                          <span>Created {sortBy === 'created_at' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('event_date')}>
                          <span>Event {sortBy === 'event_date' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-left p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('status')}>
                          <span>Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-right p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('amount')}>
                          <span>Total {sortBy === 'amount' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-right p-3 text-orange-700 font-semibold whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDiscountDisplayMode(discountDisplayMode === 'amount' ? 'percentage' : 'amount');
                              }}
                              className="text-xs px-2 py-1 rounded border border-orange-300 bg-orange-50 hover:bg-orange-100 transition-colors"
                              title={`Switch to ${discountDisplayMode === 'amount' ? 'percentage' : 'amount'} view`}
                            >
                              {discountDisplayMode === 'amount' ? '$' : '%'}
                            </button>
                            <span className="cursor-pointer hover:text-orange-800" onClick={() => handleSort('discount')}>
                              Promo {sortBy === 'discount' && (sortOrder === 'asc' ? '↑' : '↓')}
                            </span>
                          </div>
                        </th>
                        <th className="text-right p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('advance')}>
                          <span>Advance {sortBy === 'advance' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-right p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('balance')}>
                          <span>Balance {sortBy === 'balance' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-center p-3 text-orange-700 font-semibold cursor-pointer hover:bg-gray-100 whitespace-nowrap" onClick={() => handleSort('payment')}>
                          <span>Paid? {sortBy === 'payment' && (sortOrder === 'asc' ? '↑' : '↓')}</span>
                        </th>
                        <th className="text-center p-3 text-orange-700 font-semibold whitespace-nowrap">⋮</th>
                      </tr>
                    </thead>
                    <tbody>
                      {searchResults.map((order) => {
                        const balance = order.balance_amount !== undefined ? order.balance_amount : (order.total_amount - (order.advance_payment || 0) - (order.discount_amount || 0));
                        const isPaid = balance <= 0.01; // Allow for floating point tolerance
                        
                        return (
                        <tr key={order.id} className="border-b border-gray-200 hover:bg-orange-50">
                          <td className="p-3">{order.customer_info.name}</td>
                          <td className="p-3">{order.customer_info.phone}</td>
                          <td className="p-3">{formatMonDD(order.created_at)}</td>
                          <td className="p-3 whitespace-nowrap">
                            {formatMonDD(order.customer_info.start_date)}
                            {getDeliveryTime(order) && (
                              <span className="text-orange-600 ml-1">{getDeliveryTime(order)}</span>
                            )}
                          </td>
                          <td className="p-3">
                            <Badge 
                              variant={order.status === 'confirmed' ? 'default' : 'outline'}
                              className={order.status === 'confirmed' ? 'bg-green-600' : 'border-orange-300'}
                            >
                              {order.status}
                            </Badge>
                          </td>
                          <td className="p-3 text-right font-semibold">
                            ${Math.floor((order.subtotal || 0) + (order.service_fee || 0))}
                          </td>
                          <td className="p-3 text-right text-orange-600">
                            {(() => {
                              if (discountDisplayMode === 'percentage') {
                                // Calculate percentage: (discount_amount / subtotal) * 100
                                // Subtotal = total_amount + discount_amount (grand total before discount)
                                const subtotal = order.total_amount + (order.discount_amount || 0);
                                const percentage = subtotal > 0 ? ((order.discount_amount || 0) / subtotal) * 100 : 0;
                                return percentage > 0 ? `-${percentage.toFixed(1)}%` : '0%';
                              } else {
                                return order.discount_amount > 0 ? `-$${Math.floor(order.discount_amount)}` : '$0';
                              }
                            })()}
                          </td>
                          <td className="p-3 text-right text-blue-600 font-medium">
                            ${Math.floor(order.advance_payment || 0)}
                          </td>
                          <td className={`p-3 text-right font-bold text-base ${isPaid ? 'text-green-600' : 'text-red-600'}`}>
                            ${Math.floor(balance)}
                          </td>
                          <td className="p-3 text-center">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                try {
                                  await axios.put(`${API}/orders/${order.id}/toggle-payment`);
                                  // Refresh search results
                                  handleSearch();
                                } catch (error) {
                                  console.error('Failed to toggle payment:', error);
                                  toast.error('Failed to toggle payment status');
                                }
                              }}
                              className={`h-7 px-2 text-xs font-semibold ${
                                isPaid 
                                  ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200' 
                                  : 'bg-orange-100 text-orange-700 border-orange-300 hover:bg-orange-200'
                              }`}
                            >
                              {isPaid ? '✓ PAID' : '⚠️ UNPAID'}
                            </Button>
                          </td>
                          <td className="p-3">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  className="h-8 w-8 p-0"
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  const currentIndex = searchResults.findIndex(o => o.id === order.id);
                                  onViewOrder(order.id, searchResults, currentIndex);
                                }}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onEditOrder(order.id)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => onCopyOrder(order.id)}>
                                  <Copy className="mr-2 h-4 w-4" />
                                  Copy
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={async () => {
                                    try {
                                      if (window.confirm(`Are you sure you want to delete order for ${order.customer_info?.name}?`)) {
                                        await axios.delete(`${API}/orders/${order.id}`);
                                        toast.success('Order deleted successfully');
                                        handleSearch();
                                      }
                                    } catch (error) {
                                      console.error('Failed to delete order:', error);
                                      toast.error('Failed to delete order');
                                    }
                                  }}
                                  className="text-red-600"
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Delete
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="mt-4 text-center py-8 bg-gray-50 rounded border border-gray-200">
                  <p className="text-gray-600">No orders found matching &quot;<strong>{searchQuery}</strong>&quot;</p>
                  <p className="text-sm text-gray-500 mt-2">Try searching with phone number, customer name, or order ID</p>
                </div>
              )}
            </div>
          )}
        </div>
        
        {/* Powered by Caterlog Footer */}
        <div className="text-center py-6 text-sm text-gray-500">
          Powered by <span className="font-semibold text-orange-600">Caterlog</span>
        </div>
      </div>
      
      {/* Floating Contact Button */}
      <FloatingContactButton />
    </div>
  );
}

// Menu Management Page Component

export default UserLandingPage;
